"use client";

import { useCallback, useEffect, useState } from "react";
import { useTenKGame } from "@/components/dashboard/metrics-games/tenk-game-context";
import { GameConfidence } from "@/components/dashboard/metrics-games/game-confidence";
import { GameNicheBuilder } from "@/components/dashboard/metrics-games/game-niche-builder";
import { GameOpportunityScore } from "@/components/dashboard/metrics-games/game-opportunity-score";
import { GameScoreDelta } from "@/components/dashboard/metrics-games/game-score-delta";
import { GameTenKMRR } from "@/components/dashboard/metrics-games/game-tenk-mrr";
import type {
  ScoreGameInputs,
  StartupRow,
  TenKGameInputs
} from "@/components/dashboard/metrics-games/types";

const STARTUP_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const BASE_STARTUPS: StartupRow[] = [
  { id: "A", mrr: 9_200, growth30d: 14, onSale: false },
  { id: "B", mrr: 6_300, growth30d: 8, onSale: false },
  { id: "C", mrr: 4_800, growth30d: 27, onSale: true },
  { id: "D", mrr: 2_100, growth30d: -4, onSale: false },
  { id: "E", mrr: 1_300, growth30d: 3, onSale: true },
  { id: "F", mrr: 3_500, growth30d: 12, onSale: false },
  { id: "G", mrr: 5_200, growth30d: -2, onSale: true },
  { id: "H", mrr: 1_800, growth30d: 22, onSale: false },
  { id: "I", mrr: 7_100, growth30d: 5, onSale: false },
  { id: "J", mrr: 2_900, growth30d: 18, onSale: true }
];

const GAME_IDS = ["game-1", "game-2", "game-3", "game-4", "game-6"] as const;

export const GAME_TABS: Array<{ id: string; label: string }> = [
  { id: "game-1", label: "Niche builder" },
  { id: "game-2", label: "Confidence" },
  { id: "game-3", label: "Opportunity score" },
  { id: "game-4", label: "Score delta" },
  { id: "game-6", label: "$10k estimator" }
];

type Props = {
  activeGameId?: string;
  compact?: boolean;
  tenkSearchInCard?: boolean;
  opportunityScoreSearchInCard?: boolean;
  scoreDeltaSearchInCard?: boolean;
};

export function MetricsGamesContent({ activeGameId, compact = false, tenkSearchInCard = false, opportunityScoreSearchInCard = false, scoreDeltaSearchInCard = false }: Props) {
  const tenKContext = useTenKGame();
  const useTenKContext = tenKContext && (compact ? activeGameId === "game-6" : true);

  const [startups, setStartups] = useState<StartupRow[]>(BASE_STARTUPS);
  const [sampleSize, setSampleSize] = useState(11);
  const [scoreInputs, setScoreInputs] = useState<ScoreGameInputs>({
    medianMrr: 68,
    averageGrowth30d: 56,
    totalMrr: 61,
    startupCount: 64,
    forSaleRatio: 42,
    startupCountForConfidence: 11
  });
  const [previousScore, setPreviousScore] = useState(58);
  const [currentScore, setCurrentScore] = useState(66);
  const [deltaDays, setDeltaDays] = useState(7);
  const [tenKInputsLocal, setTenKInputsLocal] = useState<TenKGameInputs>({
    currentMrr: 2_200,
    startupAgeMonths: 6,
    marketMedianGrowthPerMonth: 430,
    nicheMedianMrr: 4_300,
    nicheTopQuartileMrr: 9_600,
    nicheMedianArpu: 62,
    sampleSize: 14,
    arpuOverride: 0
  });

  const tenKInputs = useTenKContext ? tenKContext.tenKInputs : tenKInputsLocal;
  const setTenKInputs = useTenKContext ? tenKContext.setTenKInputs : setTenKInputsLocal;

  function updateStartupNumber(index: number, key: "mrr" | "growth30d", value: number) {
    setStartups((prev) =>
      prev.map((startup, entryIndex) =>
        entryIndex === index
          ? { ...startup, [key]: Number.isFinite(value) ? value : 0 }
          : startup
      )
    );
  }

  function updateStartupSale(index: number, onSale: boolean) {
    setStartups((prev) =>
      prev.map((startup, entryIndex) =>
        entryIndex === index ? { ...startup, onSale } : startup
      )
    );
  }

  function removeStartup(index: number) {
    setStartups((prev) => prev.filter((_, i) => i !== index));
  }

  function addStartup() {
    const usedIds = new Set(startups.map((s) => s.id));
    const nextLetter = STARTUP_LETTERS.split("").find((c) => !usedIds.has(c));
    const newId = nextLetter ?? `X${startups.length}`;
    setStartups((prev) => [
      ...prev,
      { id: newId, mrr: 2_000, growth30d: 10, onSale: false }
    ]);
  }

  function randomizeStartups() {
    setStartups((prev) =>
      prev.map((startup) => ({
        ...startup,
        mrr: Math.floor(Math.random() * 14_000),
        growth30d: Math.floor(Math.random() * 51) - 15,
        onSale: Math.random() > 0.7
      }))
    );
  }

  function resetStartups() {
    setStartups(BASE_STARTUPS);
  }

  function updateScoreInput(key: keyof ScoreGameInputs, value: number) {
    setScoreInputs((prev) => ({
      ...prev,
      [key]: Number.isFinite(value) ? value : 0
    }));
  }

  const loadScoreInputs = useCallback((inputs: ScoreGameInputs) => {
    setScoreInputs({
      medianMrr: Number.isFinite(inputs.medianMrr) ? inputs.medianMrr : 0,
      averageGrowth30d: Number.isFinite(inputs.averageGrowth30d) ? inputs.averageGrowth30d : 0,
      totalMrr: Number.isFinite(inputs.totalMrr) ? inputs.totalMrr : 0,
      startupCount: Number.isFinite(inputs.startupCount) ? inputs.startupCount : 0,
      forSaleRatio: Number.isFinite(inputs.forSaleRatio) ? inputs.forSaleRatio : 0,
      startupCountForConfidence: Number.isFinite(inputs.startupCountForConfidence)
        ? Math.min(30, Math.max(1, inputs.startupCountForConfidence))
        : 11
    });
  }, []);

  const showOpportunityScore = compact ? activeGameId === "game-3" : true;
  useEffect(() => {
    if (!tenKContext?.registerOpportunityScoreLoader || !showOpportunityScore) return;
    return tenKContext.registerOpportunityScoreLoader(loadScoreInputs);
  }, [tenKContext, showOpportunityScore, loadScoreInputs]);

  function updateTenKInput(key: keyof TenKGameInputs, value: number) {
    const updater = (prev: TenKGameInputs) => ({
      ...prev,
      [key]: Number.isFinite(value) ? value : 0
    });
    setTenKInputs(updater);
  }

  const showAll = !activeGameId;
  const gameClass = compact ? "metrics-game-modal-slot" : "metrics-game-anchor";
  const slotClass = (id: string) =>
    showAll ? "" : id === activeGameId ? "metrics-game-slot--active" : "metrics-game-slot--hidden";

  return (
    <div className={compact ? "metrics-games-content metrics-games-content--compact" : "metrics-games-content"}>
      <div id="game-1" className={`${gameClass} ${slotClass("game-1")}`}>
        <GameNicheBuilder
            startups={startups}
            onUpdateStartupNumber={updateStartupNumber}
            onUpdateStartupSale={updateStartupSale}
            onRemoveStartup={removeStartup}
            onAddStartup={addStartup}
            onRandomize={randomizeStartups}
            onReset={resetStartups}
          />
      </div>
      <div id="game-2" className={`${gameClass} ${slotClass("game-2")}`}>
        <GameConfidence sampleSize={sampleSize} onSetSampleSize={setSampleSize} />
      </div>
      <div id="game-3" className={`${gameClass} ${slotClass("game-3")}`}>
        <GameOpportunityScore
            scoreInputs={scoreInputs}
            onUpdateScoreInput={updateScoreInput}
            searchInCard={opportunityScoreSearchInCard}
          />
      </div>
      <div id="game-4" className={`${gameClass} ${slotClass("game-4")}`}>
        <GameScoreDelta
            previousScore={previousScore}
            currentScore={currentScore}
            deltaDays={deltaDays}
            onSetPreviousScore={setPreviousScore}
            onSetCurrentScore={setCurrentScore}
            onSetDeltaDays={setDeltaDays}
            searchInCard={scoreDeltaSearchInCard}
          />
      </div>
      <div id="game-6" className={`${gameClass} ${slotClass("game-6")}`}>
        <GameTenKMRR
          tenKInputs={tenKInputs}
          onUpdateTenKInput={updateTenKInput}
          searchInCard={tenkSearchInCard}
        />
      </div>
    </div>
  );
}

export { GAME_IDS };
